import { orderBy } from 'lodash-es'
import { matchesSearchTerms, searchTerms, textTerms } from '#src/searchTerms.ts'

// Typo tolerance for word-prefix search. A search term that starts no word
// the dictionary knows is taken for a misspelling and read as the nearby
// dictionary words instead: "presure" as "pressure". A term that starts a
// known word is never second-guessed, so a correct search matches exactly as
// before.

const ALTERNATIVE_LIMIT = 8
// Below this a typo inside a prefix ("presur" for "pressur") matches too much.
const PREFIX_TYPO_MIN_LENGTH = 5

// Edits allowed for a term: none for a short word, a code or a hyphenated
// word, one for 4–6 letters, two for 7 or more.
function allowedEdits(term: string): number {
  if (!/^\p{L}+$/u.test(term) || term.length < 4) {
    return 0
  }
  return term.length < 7 ? 1 : 2
}

// Optimal string alignment distance (Levenshtein plus swapping two adjacent
// letters), giving up with `max + 1` once the distance must exceed `max`.
function editDistance(a: string, b: string, max = Infinity): number {
  if (a === b) {
    return 0
  }
  if (Math.abs(a.length - b.length) > max) {
    return max + 1
  }
  let before: number[] = []
  let previous = Array.from({ length: b.length + 1 }, (_, j) => j)
  for (let i = 1; i <= a.length; i++) {
    const current = [i]
    let rowMin = i
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      let distance = Math.min(
        (previous[j] ?? 0) + 1,
        (current[j - 1] ?? 0) + 1,
        (previous[j - 1] ?? 0) + cost
      )
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        distance = Math.min(distance, (before[j - 2] ?? 0) + 1)
      }
      current[j] = distance
      rowMin = Math.min(rowMin, distance)
    }
    if (rowMin > max) {
      return max + 1
    }
    before = previous
    previous = current
  }
  return previous[b.length] ?? 0
}

// The words a search may be spell-corrected to, bucketed by first letter.
export interface SpellingDictionary {
  // Each word's position in the texts given, which breaks ties between
  // equally near words.
  words: ReadonlyMap<string, number>
  byFirstLetter: ReadonlyMap<string, readonly string[]>
}

// A dictionary of the search terms of `texts`, as `textTerms` reads them, so
// a correction is in the same stemmed form a search is matched in. Put the
// texts whose words a correction should prefer first.
export function spellingDictionary(
  texts: Iterable<string>
): SpellingDictionary {
  const words = new Map<string, number>()
  for (const text of texts) {
    for (const word of textTerms(text)) {
      if (/^\p{L}+$/u.test(word) && word.length >= 3 && !words.has(word)) {
        words.set(word, words.size)
      }
    }
  }
  const byFirstLetter = new Map<string, string[]>()
  for (const word of words.keys()) {
    const first = word[0] ?? ''
    byFirstLetter.set(first, [...(byFirstLetter.get(first) ?? []), word])
  }
  return { words, byFirstLetter }
}

function startsKnownWord(
  term: string,
  dictionary: SpellingDictionary
): boolean {
  return (
    dictionary.words.has(term) ||
    (dictionary.byFirstLetter.get(term[0] ?? '') ?? []).some((word) =>
      word.startsWith(term)
    )
  )
}

interface TermDistance {
  distance: number
  whole: boolean
}

// How far `term` is from `word`: as a whole word, or, for a longer term, as
// the start of it with the typo inside the part typed so far.
function distanceTo(term: string, word: string, budget: number): TermDistance {
  const whole = editDistance(term, word, budget)
  if (whole <= budget || term.length < PREFIX_TYPO_MIN_LENGTH) {
    return { distance: whole, whole: true }
  }
  const prefix = Math.min(
    ...[term.length - 1, term.length, term.length + 1].flatMap((length) =>
      length < word.length
        ? [editDistance(term, word.slice(0, length), budget)]
        : []
    )
  )
  return { distance: prefix, whole: false }
}

// The dictionary words a misspelled term most likely meant, nearest first:
// fewest edits, a whole-word match ahead of a prefix one, then closest in
// length, then earliest in the dictionary. Empty for a term that starts a
// known word or allows no edits.
function spellingAlternatives(
  term: string,
  dictionary: SpellingDictionary
): string[] {
  const budget = allowedEdits(term)
  if (!budget || startsKnownWord(term, dictionary)) {
    return []
  }
  // Most typos keep the first letter; the second covers a swapped first pair.
  const candidates = new Set([
    ...(dictionary.byFirstLetter.get(term[0] ?? '') ?? []),
    ...(dictionary.byFirstLetter.get(term[1] ?? '') ?? []),
  ])
  const near = [...candidates].flatMap((word) => {
    const { distance, whole } = distanceTo(term, word, budget)
    return distance <= budget
      ? [
          {
            word,
            distance,
            whole,
            lengthGap: Math.abs(word.length - term.length),
            position: dictionary.words.get(word) ?? 0,
          },
        ]
      : []
  })
  return orderBy(
    near,
    ['distance', 'whole', 'lengthGap', 'position'],
    ['asc', 'desc', 'asc', 'asc']
  )
    .slice(0, ALTERNATIVE_LIMIT)
    .map(({ word }) => word)
}

// How well a search matches a name, best first.
export const SEARCH_MATCH = {
  // The search is the name, or its code.
  exact: 0,
  // Every term starts a word of the name.
  prefix: 1,
  // The spell-corrected search is the name.
  correctedExact: 2,
  // Every term, spell-corrected where it starts no known word, starts a word.
  fuzzy: 3,
} as const
export type SearchMatch = (typeof SEARCH_MATCH)[keyof typeof SEARCH_MATCH]

// A search read once and matched against many names.
export interface FuzzySearch {
  terms: string[]
  // Per term, what it may have meant; empty when it is taken as typed.
  alternatives: string[][]
  // The terms with each misspelling replaced by its nearest word, when any
  // was replaced, for a search engine that cannot fuzz.
  corrected?: string[]
}

export function fuzzySearch(
  input: string,
  dictionary: SpellingDictionary
): FuzzySearch {
  const terms = searchTerms(input)
  const alternatives = terms.map((term) =>
    spellingAlternatives(term, dictionary)
  )
  const corrected = terms.map((term, i) => alternatives[i]?.[0] ?? term)
  const search: FuzzySearch = { terms, alternatives }
  if (alternatives.some((words) => words.length)) {
    search.corrected = corrected
  }
  return search
}

// A name a search is matched against, read once.
export interface SearchName {
  // Its search terms as one string, to compare with a whole search.
  exact: string
  // Its words, as `textTerms` reads them, to match terms against.
  words: string[]
}

export function searchName(text: string): SearchName {
  return { exact: searchTerms(text).join(' '), words: textTerms(text) }
}

// The best match of the search against any of `names`, or undefined.
export function fuzzySearchMatch(
  search: FuzzySearch,
  names: readonly SearchName[]
): SearchMatch | undefined {
  const typed = search.terms.join(' ')
  const corrected = search.corrected?.join(' ')
  let best: SearchMatch | undefined
  for (const name of names) {
    const match = nameMatch(search, typed, corrected, name)
    if (match !== undefined && (best === undefined || match < best)) {
      best = match
    }
  }
  return best
}

function nameMatch(
  search: FuzzySearch,
  typed: string,
  corrected: string | undefined,
  { exact, words }: SearchName
): SearchMatch | undefined {
  if (typed && exact === typed) {
    return SEARCH_MATCH.exact
  }
  if (matchesSearchTerms(search.terms, words)) {
    return SEARCH_MATCH.prefix
  }
  if (corrected === undefined) {
    return undefined
  }
  if (exact === corrected) {
    return SEARCH_MATCH.correctedExact
  }
  const fuzzy = search.terms.every((term, i) =>
    words.some(
      (word) =>
        word.startsWith(term) ||
        (search.alternatives[i] ?? []).some((alternative) =>
          word.startsWith(alternative)
        )
    )
  )
  return fuzzy ? SEARCH_MATCH.fuzzy : undefined
}

// The edit budget and distance, exposed only for their unit tests.
export const _fuzzyTermsForTesting = { allowedEdits, editDistance }
